import React, {CSSProperties, ReactElement} from 'react';
import {Classes, Popover} from "@blueprintjs/core";
import classNames from "classnames";
import './coloredBar.scss'
import _ from "lodash";

export interface Segment {
    item?: any,
    value: number,
    percent?: number,
    color: string,
    text?: string,
    textStyle?: React.CSSProperties
};

interface ColoredProgressBarProps {
    segments?: Segment[],
    style?: any,
    className?: any,
    intent?: any,
    computeRatioFn?: (percent: number, value: number, total: number) => number, // function
    minWidth?: number,
    minWidthUnit?: 'px' | '%' | 'em',
    tooltipContent?: (segment: Segment) => ReactElement,
    onClickSegment?: (event, segment: Segment) => void,
    showPercentages?: boolean,
}

export class ColoredBar extends React.PureComponent<ColoredProgressBarProps> {

    static defaultProps = {};


    render() {
        const {
            segments,
            className,
            intent,
            style,
            minWidth,
            minWidthUnit,
            tooltipContent,
            showPercentages,
            onClickSegment
        } = this.props;
        const total = segments.map(value => value == null || value.value == null ? 0 : value.value)
            .reduce((previousValue, currentValue) => currentValue + previousValue, 0);

        const values = segments
            .filter(({percent, value}) => this.getRatio(percent, value, total))
            .map((segment, index) => {
                return {
                    ...segment,
                    ratio: this.getRatio(segment.percent, segment.value, total),
                    style: {
                        backgroundColor: segment.color,
                        color: segment.color
                    }
                };
            });
        const classes = classNames(
            Classes.intentClass(intent),
            'colored-bar',
            className,
        );

        const has2Value = values.filter((v) => !!v && v.ratio > 0).length == 2;
        const has3Value = values.filter((v) => !!v && v.ratio > 0).length == 3;
        const justifyContent = (has2Value || has3Value? 'space-between': "unset")

        const coloredBar = <div className={'colored-bar-container'}>
            <div className={classes} style={{...style}}>
                {
                    values?.map((value, index) => {

                        const isLast = index === values.length - 1;
                        const isfirst = index === 0;

                        const content = <div style={{height: '100%', width: '100%'}}>
                            {value.text ? <div style={value.textStyle}> {value.text}</div> : null}</div>

                        const segmentContent = <>{tooltipContent ?
                            <Popover className="popover" content={tooltipContent(value)} interactionKind="hover"
                                     placement='auto'>
                                {content}
                            </Popover> : <>{content}</>}</>
                        // don't set width if value is null (rely on default CSS value)
                        const width = value == null ? null : 100 * this.clamp(value.ratio, 0, 1) + "%";
                        const segment = <div
                            className={'item' + (isLast ? ' last' : "") + (isfirst ? ' first' : "")}
                            style={{
                                ...value.style,
                                width,
                                height: "inherit",
                                position: "unset",
                                minWidth: minWidth ? minWidth + (minWidthUnit || '%') : 'unset'
                            }}
                            onClick={(event) => onClickSegment ? onClickSegment(event, value) : null}
                            key={index}>
                            {segmentContent}
                        </div>;
                        return segment
                    })
                }
            </div>
            {showPercentages && (
                <div className="colored-bar-percent" style={{...style, justifyContent}}>
                    {
                        values?.map((value, index) => {

                            const isLast = index === values.length - 1;
                            const isfirst = index === 0;


                            // don't set width if value is null (rely on default CSS value)
                            const width = value == null ? null : 100 * this.clamp(value.ratio, 0, 1) + "%";
                            const style: CSSProperties = {
                                width:has2Value || has3Value? 'unset': width,
                                position: "unset",
                                minWidth: minWidth ? minWidth + '%' : 'unset',
                                color: value.style.color
                            };

                            return value.ratio > 0 ? <div
                                className={'item' + (isLast ? ' last' : "") + (isfirst ? ' first' : "")}
                                style={style}
                                key={index + '_percent'}>
                                {_.round(value.ratio * 100, 2) + '%'}
                            </div> : null;
                        })
                    }
                </div>)}
        </div>;
        return coloredBar;
    }

    private getRatio(percent: number, value: number, total: number): number {
        if (this.props.computeRatioFn) {
            return this.props.computeRatioFn(percent, value, total);
        }
        if (percent === undefined || percent == null)
            return (value == null || value == null || value === 0 ? null : value / total);
        return percent / 100;
    }

    clamp(val, min, max) {
        if (val == null) {
            return val;
        }
        if (max < min) {
            throw new Error('min cannot be greater than max');
        }
        return Math.min(Math.max(val, min), max);
    }
}